import { useCallback, useLayoutEffect } from 'react';
import type { AutoGrowTextAreaOptions } from '../types/auto-grow-text-area.types';

const measurementProperties = [
  'box-sizing',
  'width',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'font-stretch',
  'font-variant',
  'font-feature-settings',
  'font-variation-settings',
  'line-height',
  'letter-spacing',
  'word-spacing',
  'text-indent',
  'text-transform',
  'text-rendering',
  'text-align',
  'direction',
  'tab-size',
  'white-space',
  'word-break',
  'overflow-wrap',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'border-style',
];

// Never reset the live editor to its row minimum: a native IME can react to
// that temporary caret geometry even if the browser paints only the final size.
const measureContent = (
  element: HTMLTextAreaElement,
  style: CSSStyleDeclaration
) => {
  const measurement = element.ownerDocument.createElement('textarea');
  measurement.setAttribute('aria-hidden', 'true');
  measurement.tabIndex = -1;
  measurement.readOnly = true;
  measurement.rows = element.rows;
  measurement.wrap = element.wrap;
  measurement.placeholder = element.placeholder;
  measurement.value = element.value;
  measurementProperties.forEach((property) => {
    measurement.style.setProperty(property, style.getPropertyValue(property));
  });
  Object.assign(measurement.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    height: 'auto',
    minHeight: '0',
    maxHeight: 'none',
    overflow: 'hidden',
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  element.ownerDocument.body.append(measurement);
  try {
    return measurement.scrollHeight;
  } finally {
    measurement.remove();
  }
};

/**
 * Fits text while respecting row and optional layout limits, then scrolls inside.
 * @param options - Native control and feature-independent sizing constraints.
 * @returns A resize callback for controlled and uncontrolled content changes.
 */
export const useAutoGrowTextArea = ({
  elementRef,
  enabled,
  maxRows,
  heightBoundary,
}: AutoGrowTextAreaOptions) => {
  const resize = useCallback(() => {
    const element = elementRef.current;
    if (!enabled || !element) return;
    const previousHeight = element.clientHeight;
    const contentHeight = element.scrollHeight;
    const atEnd =
      element.ownerDocument.activeElement === element &&
      element.selectionStart === element.selectionEnd &&
      element.selectionEnd === element.value.length;
    const retainBottom =
      previousHeight > 0 &&
      contentHeight - previousHeight - element.scrollTop <= 1 &&
      (contentHeight > previousHeight || atEnd);
    const style = getComputedStyle(element);
    const lineHeight =
      Number.parseFloat(style.lineHeight) ||
      (Number.parseFloat(style.fontSize) || 16) * 1.5;
    const padding =
      (Number.parseFloat(style.paddingTop) || 0) +
      (Number.parseFloat(style.paddingBottom) || 0);
    const border =
      (Number.parseFloat(style.borderTopWidth) || 0) +
      (Number.parseFloat(style.borderBottomWidth) || 0);
    let maximum = maxRows
      ? Math.max(1, maxRows) * lineHeight + padding + border
      : Infinity;
    const container = heightBoundary?.containerRef.current;
    const flexible = heightBoundary?.flexibleRef.current;
    if (container && flexible) {
      // Share fractional geometry with the inline panel. Rounded client/offset
      // heights otherwise let the editor consume pixels reserved for the dock.
      const flexibleBoxHeight = flexible.getBoundingClientRect().height;
      const flexibleHeight =
        flexibleBoxHeight > 0
          ? Math.max(
              0,
              flexibleBoxHeight -
                (flexible.offsetHeight - flexible.clientHeight)
            )
          : flexible.clientHeight;
      const elementHeight =
        element.getBoundingClientRect().height || element.offsetHeight;
      const overflow = Math.max(
        0,
        container.scrollHeight - container.clientHeight
      );
      maximum = Math.min(
        maximum,
        Math.max(
          lineHeight + padding + border,
          elementHeight +
            flexibleHeight -
            heightBoundary.minimumFlexibleHeight -
            overflow
        )
      );
    }
    const required = measureContent(element, style) + border;
    element.style.height = `${Math.min(required, maximum)}px`;
    element.style.overflowY = required > maximum ? 'auto' : 'hidden';
    // Native focus scrolling can finish before the keyboard reduces our budget.
    // Keep an already-visible end visible, without pulling readers from earlier lines.
    if (retainBottom && element.clientHeight < previousHeight) {
      element.scrollTop = element.scrollHeight;
    }
  }, [elementRef, enabled, maxRows, heightBoundary]);

  useLayoutEffect(() => {
    if (!enabled) return;
    resize();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(resize);
    const targets = [
      elementRef.current,
      heightBoundary?.containerRef.current,
      heightBoundary?.flexibleRef.current,
    ];
    targets.forEach((target) => {
      if (target) observer?.observe(target);
    });
    window.addEventListener('resize', resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [elementRef, enabled, heightBoundary, resize]);

  return resize;
};
